import React, { useEffect, useRef, useState } from 'react'
import '@whereby.com/browser-sdk'
import styled from 'styled-components'
import { LiveInterviewType } from 'types'
import { formatDateTimeTimezone } from 'utils/date'
import { H2 } from 'components/styled/typography'
import rrLogo from 'components/styled/assets/recright-logo.svg'
import Error404 from 'components/errors/404'
import Error403 from 'components/errors/403'
import { connect } from 'react-redux'
import { clearIntercomCookies } from 'chat-bots/intercom'
import { ThemeSpace } from 'theme/spaces'
import { I18nComponentType } from 'i18n-types'
import { mapLanguageToComponentProps } from 'i18n'

interface LiveRoomProps {
  liveInterview: LiveInterviewType
  roomUrl?: string
  participant?: {
    name?: string
    email: string
    language?: string
    roomCode?: string
  }
  error: number | null
  i18n: I18nComponentType
}

const LiveContainer = styled.div`
  height: 100vh;
  width: 100vw;

  iframe {
    width: 100vw;
    height: 100vh;
  }

  justify-content: center;
  display: flex;
  background: ${({ theme }) => theme.color.primary05};
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  text-align: center;
  margin-top: ${({ theme }) => theme.space.s40};
  padding: ${({ theme }) => theme.space.s40};
`

const StyledRRLogo = styled.img`
  max-width: ${({ theme }) => theme.space.s240};
  margin-bottom: ${({ theme }) => theme.space.s32};

  ${({ theme }) => theme.mediaQuery.phone} {
    max-width: ${({ theme }) => theme.space.s128};
    margin-bottom: ${({ theme }) => theme.space.s20};
  }
`

const StyledText = styled(H2)<{ size?: ThemeSpace }>`
  color: ${({ theme }) => theme.color.primary120};
  margin-bottom: ${({ theme }) => theme.space.s16};
  line-height: 1.5;
  font-size: ${({ theme, size }) =>
    size ? theme.space[size] : theme.space.s24};
  font-family: ${({ theme }) => theme.font.primary};
`

const mapStateToProps = (state: any) => ({
  error: state.global.httpError,
})

function LiveRoom({
  liveInterview,
  roomUrl,
  participant,
  error,
  i18n,
}: LiveRoomProps) {
  const [timeLeft, setTimeLeft] = useState<string | null>(null)
  const [started, setStarted] = useState(false)
  const timerRef = useRef<null | ReturnType<typeof setInterval>>(null)
  const globalWindow: any = window

  useEffect(() => {
    if (!liveInterview?.startTime) return

    const startTime = new Date(liveInterview.startTime).getTime()

    const updateCountdown = () => {
      const now = new Date().getTime()
      const difference = startTime - now

      if (difference <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current)
        }
        setStarted(true)
        setTimeLeft(null) // Interview has started
        return
      }

      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
      const minutes = Math.floor((difference / (1000 * 60)) % 60)
      const seconds = Math.floor((difference / 1000) % 60)

      setTimeLeft(`${hours}h ${minutes}m ${seconds}s`) // should have localization
    }

    timerRef.current = setInterval(updateCountdown, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [liveInterview?.startTime])

  useEffect(() => {
    if (globalWindow && globalWindow.Intercom) {
      globalWindow.Intercom('shutdown')
    }
    clearIntercomCookies(true)
  }, [globalWindow.Intercom])

  if (error === 403) {
    return <Error403 />
  }
  if (error || !liveInterview) {
    return <Error404 />
  }

  if (new Date(liveInterview?.startTime) > new Date()) {
    const formattedStartTime = formatDateTimeTimezone(
      liveInterview?.startTime,
      liveInterview?.timezone
    )

    return (
      <LiveContainer>
        <InfoContainer>
          <StyledRRLogo src={rrLogo} alt="recright-logo" width={200} />
          {participant && (
            <H2 color="primary120">
              {i18n.liveRoom.greeting(participant.name || participant.email)}
            </H2>
          )}
          {liveInterview.title && (
            <StyledText>
              {i18n.liveRoom.interviewTitle(liveInterview.title)}
            </StyledText>
          )}
          <StyledText>
            {i18n.liveRoom.notYetStarted(formattedStartTime)}
          </StyledText>
          {timeLeft && (
            <StyledText size="s32">
              {i18n.liveRoom.interviewStartsIn(timeLeft)}
            </StyledText>
          )}
        </InfoContainer>
      </LiveContainer>
    )
  }

  if (liveInterview.status === 'finished' || liveInterview.isFinished) {
    return (
      <LiveContainer>
        <InfoContainer>
          <StyledRRLogo src={rrLogo} alt="recright-logo" width={200} />
          <StyledText>{i18n.liveRoom.interviewHasEnded}</StyledText>
        </InfoContainer>
      </LiveContainer>
    )
  }
  if (roomUrl && started && participant) {
    return (
      <LiveContainer>
        {/* @ts-ignore */}
        <whereby-embed
          breakout="off"
          locking="off"
          moreButton="off"
          participantCount="off"
          pipButton="off"
          timer="off"
          topToolbar="off"
          subgridLabels="off"
          toolbarDarkText="on"
          lang={participant.language || 'en'}
          displayName={participant.name || participant.email} // should we allow display name if no name?
          room={roomUrl}
        />
      </LiveContainer>
    )
  }
  return null
}

export default connect(mapLanguageToComponentProps(mapStateToProps))(LiveRoom)
